import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { admin } from '../../api/client.js';
import {
  PageHeader, SearchBox, FilterChips, SortHeader, useSort, useTextFilter,
  EmptyState, ConfirmDialog, Modal, FormSection, Badge, DateTime
} from '../../components/admin/AdminUI.jsx';

const SEARCH_FIELDS = ['email', 'fullName', 'jobRole', 'profileName', 'defaultRlsUsername'];

/**
 * What the "Mobile last used" cell says on hover.
 *
 * Both dates are worth having and they answer different questions: last used
 * is "are they getting value from the app", first sign-in is "when did this
 * credential start existing", which is the one that matters when somebody
 * leaves. The column shows the first because it is the one people mean.
 */
function mobileTooltip(u) {
  const parts = [];
  if (u.lastMobileSignInUtc) {
    parts.push(`Signed in ${new Date(u.lastMobileSignInUtc).toLocaleDateString('en-GB')}`);
  }
  parts.push(u.activeDeviceCount
    ? `${u.activeDeviceCount} device${u.activeDeviceCount === 1 ? '' : 's'} still signed in`
    : 'No device currently signed in');
  return parts.join(' · ');
}

/**
 * Phones and tablets this person is currently signed in on.
 *
 * Read-only. Revoking a named device is deliberately not here yet — showing an
 * administrator that a phone exists is the part worth having now, because
 * "who has this on a phone?" is the question asked when somebody leaves, and
 * the answer today is "nobody knows". Deactivating the user revokes every
 * device, which is the blunt instrument that already works.
 *
 * A device name is whatever the app reported, so it is a label rather than
 * evidence.
 */
function MobileDevices({ devices }) {
  // Absent while the user loads, and on a build that predates the field.
  if (!devices) return null;

  return (
    <FormSection
      title="Mobile devices"
      hint="Signed in through the Vizora app. Deactivating this user signs out every device.">
      {devices.length === 0
        ? <p className="admin-muted small mb-0">No device is currently signed in.</p>
        : (
          <div className="admin-table-wrap">
            <table className="table table-sm admin-table mb-0">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Signed in</th>
                  <th>Last used</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d, i) => (
                  <tr key={i}>
                    <td>
                      <i className={`bi ${d.platform === 'ios' ? 'bi-apple' : 'bi-android2'} me-2`} />
                      {d.name || <span className="admin-muted">Unnamed device</span>}
                    </td>
                    <td className="small text-nowrap"><DateTime value={d.signedInUtc} /></td>
                    <td className="small text-nowrap"><DateTime value={d.lastSeenUtc} /></td>
                    <td className="small text-nowrap"><DateTime value={d.expiresUtc} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </FormSection>
  );
}

export default function AdminUsers() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);   // null | 'new' | userId
  const [confirming, setConfirming] = useState(null);
  const [term, setTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const { sort, toggle, sortRows } = useSort('email');

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin', 'users'], queryFn: admin.listUsers
  });
  const { data: profiles = [] } = useQuery({
    queryKey: ['admin', 'profiles'], queryFn: admin.listProfiles
  });

  // ['admin', 'user', id] is not prefix-matched by ['admin', 'users'], so the
  // edit dialog served stale data after a save. Invalidate the whole subtree.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin'] });
    queryClient.invalidateQueries({ queryKey: ['navigation'] });
  };
  const remove = useMutation({
    mutationFn: admin.deleteUser,
    onSuccess: () => { setConfirming(null); invalidate(); }
  });

  const counts = useMemo(() => ({
    all: users.length,
    admins: users.filter(u => u.isAdmin).length,
    pending: users.filter(u => u.isActive && !u.isProvisioned).length,
    inactive: users.filter(u => !u.isActive).length
  }), [users]);

  const byFilter = useMemo(() => ({
    all: users,
    admins: users.filter(u => u.isAdmin),
    pending: users.filter(u => u.isActive && !u.isProvisioned),
    inactive: users.filter(u => !u.isActive)
  }[filter] ?? users), [users, filter]);

  const searched = useTextFilter(byFilter, term, SEARCH_FIELDS);
  const rows = sortRows(searched, {
    email: u => u.email,
    fullName: u => u.fullName,
    jobRole: u => u.jobRole,
    profileName: u => u.profileName,
    isAdmin: u => (u.isAdmin ? 1 : 0),
    lastLoginUtc: u => (u.lastLoginUtc ? new Date(u.lastLoginUtc).getTime() : null),
    // Sorting by last USE is how you find who has actually adopted the app,
    // and whose phone is signed in but has not been opened in months.
    lastMobileSeenUtc: u =>
      (u.lastMobileSeenUtc ? new Date(u.lastMobileSeenUtc).getTime() : null)
  });

  return (
    <div className="admin-page">
      <PageHeader title="User Setup" count={users.length}>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing('new')}>
          <i className="bi bi-plus-lg" /> Add user
        </button>
      </PageHeader>

      <div className="admin-toolbar">
        <SearchBox value={term} onChange={setTerm}
                   placeholder="Search name, email, profile…" />
        <FilterChips
          value={filter} onChange={setFilter}
          options={[
            { value: 'all',      label: 'All',           count: counts.all },
            { value: 'admins',   label: 'Admins',        count: counts.admins },
            { value: 'pending',  label: 'Invite pending', count: counts.pending },
            { value: 'inactive', label: 'Disabled',      count: counts.inactive }
          ]}
        />
      </div>

      {isLoading
        ? <div className="text-center py-5"><div className="spinner-border" /></div>
        : rows.length === 0
          ? <EmptyState
              icon={users.length ? 'bi-search' : 'bi-people'}
              title={users.length ? 'No users match those filters' : 'No users yet'}
            >
              {users.length
                ? 'Try a different search term, or clear the filter.'
                : 'Add a user to send them an Entra invitation and give them access.'}
            </EmptyState>
          : (
            <div className="admin-table-wrap">
              <table className="table table-sm admin-table mb-0">
                <thead>
                  <tr>
                    <SortHeader label="User"     sortKey="fullName"     sort={sort} onToggle={toggle} />
                    <SortHeader label="Job role" sortKey="jobRole"      sort={sort} onToggle={toggle} />
                    <SortHeader label="Profile"  sortKey="profileName"  sort={sort} onToggle={toggle} />
                    <th>RLS username</th>
                    <SortHeader label="Access"   sortKey="isAdmin"      sort={sort} onToggle={toggle} />
                    <SortHeader label="Last sign-in" sortKey="lastLoginUtc" sort={sort} onToggle={toggle} />
                    <SortHeader label="Mobile last used" sortKey="lastMobileSeenUtc" sort={sort} onToggle={toggle} />
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(u => (
                    <tr key={u.userId}>
                      <td>
                        <div className="fw-medium">{u.fullName || <span className="admin-muted">—</span>}</div>
                        <div className="admin-muted small">{u.email}</div>
                      </td>
                      <td className="small">{u.jobRole || <span className="admin-muted">—</span>}</td>
                      <td className="small">
                        {u.profileName ?? <span className="admin-muted">unassigned</span>}
                      </td>
                      <td className="admin-mono admin-muted">{u.defaultRlsUsername || '—'}</td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          {u.isAdmin && <Badge tone="primary">Admin</Badge>}
                          {!u.isActive && <Badge tone="secondary">Disabled</Badge>}
                          {/* Answers "who has the assistant?" from the list.
                              Without it the only way to find out is to open
                              every user in turn. Shown when on, because on is
                              the exceptional state now that both flags
                              default to off. */}
                          {u.isAiEnabled && (
                            <Badge tone="info"
                                   title="Enabled for this user. The profile must allow it too.">
                              AI
                            </Badge>
                          )}
                          {u.isActive && !u.isProvisioned &&
                            <Badge tone="warning" title="No Entra identity bound yet — binds on first sign-in">
                              Invite pending
                            </Badge>}
                          {u.isActive && u.isProvisioned && !u.isAdmin &&
                            <Badge tone="success">Active</Badge>}
                        </div>
                      </td>
                      <td className="small text-nowrap">
                        <DateTime value={u.lastLoginUtc} />
                      </td>
                      {/* Last USED, not last signed in. A mobile session lasts
                          30 days and renews itself, so the sign-in date reads
                          as months ago for somebody using the app daily - which
                          is the opposite of what this column is for. The
                          sign-in date is still in the tooltip, and per device
                          in the edit dialog. */}
                      <td className="small text-nowrap">
                        {u.lastMobileSeenUtc
                          ? (
                            <span title={mobileTooltip(u)}>
                              <i className={u.activeDeviceCount
                                ? 'bi bi-phone me-1'
                                : 'bi bi-phone-slash me-1 admin-muted'} />
                              <DateTime value={u.lastMobileSeenUtc} />
                            </span>
                          )
                          : <span className="admin-muted">—</span>}
                      </td>
                      <td className="row-actions">
                        <button type="button" className="btn btn-sm btn-link"
                                title="Edit user" aria-label={`Edit ${u.email}`}
                                onClick={() => setEditing(u.userId)}>
                          <i className="bi bi-pencil" />
                        </button>
                        <button type="button" className="btn btn-sm btn-link text-danger"
                                title="Delete user" aria-label={`Delete ${u.email}`}
                                onClick={() => setConfirming(u)}>
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

      {editing && (
        <UserDialog
          userId={editing === 'new' ? null : editing}
          profiles={profiles}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); invalidate(); }}
        />
      )}

      {confirming && (
        <ConfirmDialog
          title="Delete this user?"
          confirmLabel="Delete user"
          busy={remove.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => remove.mutate(confirming.userId)}
          body={
            <>
              <p className="mb-2">
                <strong>{confirming.fullName || confirming.email}</strong> will lose access
                immediately and any active sessions will be ended.
              </p>
              <p className="mb-0 small admin-muted">
                Their guest account is also removed from the directory. Their saved
                views and favourites are deleted. This cannot be undone — to keep the
                record, set them to inactive instead.
              </p>
            </>
          }
        />
      )}
    </div>
  );
}

/* ------------------------------ dialog -------------------------------- */

function UserDialog({ userId, profiles, onClose, onSaved }) {
  const isNew = !userId;
  const { data: existing } = useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: () => admin.getUser(userId),
    enabled: !isNew
  });
  const { data: catalogue } = useQuery({
    queryKey: ['admin', 'catalogue'], queryFn: admin.getCatalogue
  });

  const [form, setForm] = useState({
    email: '', fullName: '', jobRole: '', profileId: '',
    defaultRlsUsername: '', isAdmin: false, isActive: true, isAiEnabled: false,
    datasetRoles: [],
    // Default on: someone new to the directory is the common case, and this
    // matches the behaviour before the option existed.
    sendInvitation: true
  });
  const [loaded, setLoaded] = useState(isNew);
  const [error, setError] = useState(null);

  if (existing && !loaded) {
    setForm({
      email: existing.email,
      fullName: existing.fullName ?? '',
      jobRole: existing.jobRole ?? '',
      profileId: existing.profileId ?? '',
      defaultRlsUsername: existing.defaultRlsUsername ?? '',
      isAdmin: existing.isAdmin,
      isActive: existing.isActive,
      isAiEnabled: existing.isAiEnabled ?? false,
      datasetRoles: existing.datasetRoles ?? []
    });
    setLoaded(true);
  }

  const save = useMutation({
    mutationFn: (payload) => isNew ? admin.createUser(payload) : admin.updateUser(userId, payload),
    onSuccess: onSaved,
    onError: (err) => setError({
      // details.fix is set by the API for directory failures, where the cause
      // is nearly always a missing Graph permission the admin can grant.
      message: err.body?.issues?.map(i => `${i.path}: ${i.message}`).join('; ')
        ?? err.body?.error ?? err.message,
      fix: err.body?.details?.fix ?? null
    })
  });

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const submit = (e) => {
    e.preventDefault();
    setError(null);
    const payload = {
      ...form,
      profileId: form.profileId || null,
      fullName: form.fullName || null,
      jobRole: form.jobRole || null,
      defaultRlsUsername: form.defaultRlsUsername || null,
      /* Both halves are required, so a half-filled row is dropped here rather
         than sent as an identity with no role or no username - either would be
         accepted and then produce the wrong rows for that user. Done at submit
         because doing it per keystroke is what made the fields untypeable. */
      datasetRoles: form.datasetRoles.filter(r => r.rlsRole && r.rlsUsername)
    };
    if (!isNew) delete payload.email;
    save.mutate(payload);
  };

  const rlsDatasets = (catalogue?.datasets ?? []).filter(d => d.requiresRls);

  return (
    <Modal
      title={isNew ? 'Add user' : `Edit ${existing?.fullName || existing?.email || 'user'}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="user-form" className="btn btn-sm btn-primary"
                  disabled={save.isPending}>
            {save.isPending ? 'Saving…' : (isNew ? 'Create and invite' : 'Save changes')}
          </button>
        </>
      }
    >
      <form id="user-form" className="modal-body" onSubmit={submit}>
        {error && (
          <div className="alert alert-danger py-2 small">
            <div>{error.message}</div>
            {error.fix && <div className="mt-1 fw-semibold">{error.fix}</div>}
          </div>
        )}

        <FormSection title="Identity"
                     hint={isNew
                       ? 'The email must match the account they will sign in with.'
                       : 'Email is fixed — it is the link to their Entra identity.'}>
          <div className="row g-3">
            <div className="col-md-7">
              <label className="form-label" htmlFor="email">Email address</label>
              <input id="email" className="form-control form-control-sm" type="email" required
                     value={form.email} disabled={!isNew} autoFocus={isNew}
                     onChange={e => set({ email: e.target.value })} />
            </div>
            <div className="col-md-5">
              <label className="form-label" htmlFor="fullName">Full name</label>
              <input id="fullName" className="form-control form-control-sm" value={form.fullName}
                     onChange={e => set({ fullName: e.target.value })} />
            </div>
            <div className="col-md-5">
              <label className="form-label" htmlFor="jobRole">Job role</label>
              <input id="jobRole" className="form-control form-control-sm" value={form.jobRole}
                     placeholder="Shown under their name in the header"
                     onChange={e => set({ jobRole: e.target.value })} />
            </div>

            {/* Off when the person is already a guest in the directory. The
                invite is then redundant, and its failure used to roll the
                whole user back. Sign-in binds their object id by email on
                first arrival either way. */}
            {isNew && (
              <div className="col-12">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="sendInvitation"
                         checked={form.sendInvitation}
                         onChange={e => set({ sendInvitation: e.target.checked })} />
                  <label className="form-check-label" htmlFor="sendInvitation">
                    Send an Entra B2B invitation
                  </label>
                </div>
                <div className="form-text">
                  {form.sendInvitation
                    ? 'Leave ticked for someone new to the directory.'
                    : 'They already have access, so no invitation is sent. They can sign in as soon as this is saved.'}
                </div>
              </div>
            )}
          </div>
        </FormSection>

        <FormSection title="Access"
                     hint="The profile decides which reports appear in their menu.">
          <div className="row g-3 align-items-end">
            <div className="col-md-6">
              <label className="form-label" htmlFor="profileId">Profile</label>
              <select id="profileId" className="form-select form-select-sm" value={form.profileId}
                      onChange={e => set({ profileId: e.target.value })}>
                <option value="">— unassigned (empty menu) —</option>
                {profiles.map(p => (
                  <option key={p.profileId} value={p.profileId}>
                    {p.name}{p.itemCount ? ` — ${p.itemCount} item${p.itemCount === 1 ? '' : 's'}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 d-flex gap-4 pb-1">
              <div className="form-check">
                <input id="isAdmin" className="form-check-input" type="checkbox"
                       checked={form.isAdmin}
                       onChange={e => set({ isAdmin: e.target.checked })} />
                <label className="form-check-label" htmlFor="isAdmin">Administrator</label>
              </div>
              <div className="form-check">
                <input id="isActive" className="form-check-input" type="checkbox"
                       checked={form.isActive}
                       onChange={e => set({ isActive: e.target.checked })} />
                <label className="form-check-label" htmlFor="isActive">Active</label>
              </div>
              <div className="form-check">
                <input id="isAiEnabled" className="form-check-input" type="checkbox"
                       checked={form.isAiEnabled}
                       onChange={e => set({ isAiEnabled: e.target.checked })} />
                <label className="form-check-label" htmlFor="isAiEnabled">
                  Vizora AI Data Assistant
                </label>
              </div>
            </div>
          </div>
          {form.isAdmin && (
            <p className="form-text mt-2 mb-0">
              Administrators can manage every user and profile for this customer.
            </p>
          )}
          {/* Said here rather than discovered later. Ticking this alone does
              nothing if the profile's box is clear, and an administrator who
              is not told that will tick it, test it, and report a bug. */}
          <p className="form-text mt-2 mb-0">
            {form.isAiEnabled
              ? 'The assistant also has to be enabled on this user\u2019s profile, '
                + 'under Profile Management, before it appears for them.'
              : 'With this clear, the assistant is shown in their menu marked '
                + '\u201cOff\u201d and cannot be opened.'}
          </p>
        </FormSection>

        <MobileDevices devices={existing?.devices} />

        <FormSection
          title="Row-level security"
          hint="Only datasets that require an effective identity are listed. Leave blank to use the default.">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label" htmlFor="rls">Default RLS username</label>
              <input id="rls" className="form-control form-control-sm admin-mono"
                     value={form.defaultRlsUsername}
                     placeholder="e.g. their department code"
                     onChange={e => set({ defaultRlsUsername: e.target.value })} />
              <div className="form-text">
                Used for any RLS dataset without a specific override below.
              </div>
            </div>
          </div>

          {rlsDatasets.length > 0 ? (
            <table className="table table-sm mt-3 mb-0">
              <thead>
                <tr><th>Dataset</th><th style={{ width: '28%' }}>Role</th><th style={{ width: '28%' }}>Username</th></tr>
              </thead>
              <tbody>
                {rlsDatasets.map(ds => {
                  const row = form.datasetRoles.find(r => r.datasetId === ds.id) ?? {};
                  /* Keep what is typed, even while it is incomplete.
                     ---------------------------------------------------------
                     This used to drop the row unless BOTH halves were filled,
                     which made the fields impossible to use: typing the first
                     character of the role left the username empty, the row was
                     discarded, `row` fell back to {} and the input reset to ''.
                     Every keystroke deleted itself.

                     The intent was right - a half-configured identity must not
                     reach the server - but that belongs at submit, where the
                     value is finished, not on every character. Incomplete rows
                     are filtered in `submit`. */
                  const update = (patch) => {
                    const others = form.datasetRoles.filter(r => r.datasetId !== ds.id);
                    const merged = { datasetId: ds.id, rlsRole: '', rlsUsername: '', ...row, ...patch };
                    // An emptied row is removed rather than kept as blanks, so
                    // clearing both boxes still means "no override".
                    const empty = !merged.rlsRole && !merged.rlsUsername;
                    set({ datasetRoles: empty ? others : [...others, merged] });
                  };
                  return (
                    <tr key={ds.id}>
                      <td className="small">{ds.name}</td>
                      <td>
                        <input className="form-control form-control-sm" value={row.rlsRole ?? ''}
                               placeholder="Department"
                               onChange={e => update({ rlsRole: e.target.value })} />
                      </td>
                      <td>
                        <input className="form-control form-control-sm admin-mono"
                               value={row.rlsUsername ?? ''}
                               onChange={e => update({ rlsUsername: e.target.value })} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="form-text mt-2 mb-0">
              No datasets in this workspace require row-level security.
            </p>
          )}
        </FormSection>
      </form>
    </Modal>
  );
}
